// @ts-nocheck
/**
 * CyberSweep My Work
 *
 * Shows the current user's assigned CS Audits and work queue.
 * Prefers a one-hop Gobodo traversal (auditor USER → CSAudit via cs_audit_auditor_rel), with union
 * branches on indexed auditor fields when SF user id is known; then index-only traversal on those
 * fields if needed; otherwise lists all audits and filters by auditor vs the current user.
 */

import React, { useMemo, useState } from 'react';
import { Briefcase, Clock, AlertTriangle, CheckCircle2, Calendar, FileText, ChevronRight } from 'lucide-react';
import {
  useCSAudits,
  useMyWorkCSAudits,
  useAccountIdToName,
  getCsAuditClientAccountRef,
  resolveCSAuditListClientName,
} from '../../services/queries/cyberSweepQueries';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { StandardSelect, StandardSearchBar } from '../../components/standard';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { formatCyberSweepDisplayDate, parseCyberSweepDateToMs } from '../../utils/cyberSweepDisplayDate';
import { getCustomSuffix } from '../../config/gobodoConfig';

interface CyberSweepMyWorkProps {
  onViewAudit?: (auditId: string) => void;
  onBack?: () => void;
}

// Field resolver for multi-name fields
const g = (r: Record<string, unknown>, ...keys: string[]): unknown => {
  for (const k of keys) {
    if (r[k] !== undefined && r[k] !== null) return r[k];
  }
  return undefined;
};

const isOverdue = (rawDue: unknown): boolean => {
  const ms = parseCyberSweepDateToMs(rawDue);
  if (ms == null) return false;
  const dueDate = new Date(ms);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dueDate < today;
};

const isDueThisWeek = (rawDue: unknown): boolean => {
  const ms = parseCyberSweepDateToMs(rawDue);
  if (ms == null) return false;
  const dueDate = new Date(ms);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const weekFromNow = new Date(today);
  weekFromNow.setDate(weekFromNow.getDate() + 7);
  return dueDate >= today && dueDate <= weekFromNow;
};

const getPhaseStatusColor = (status: string) => {
  if (status?.includes('Completed') || status?.includes('Complete'))
    return 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200';
  if (status?.includes('In Progress'))
    return 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200';
  if (status?.includes('Road Block'))
    return 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200';
  return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
};

const CyberSweepMyWork: React.FC<CyberSweepMyWorkProps> = ({ onViewAudit, onBack }) => {
  const { userId, userName } = useCurrentUser();
  const myWorkQuery = useMyWorkCSAudits({ enabled: USE_GOBODO_API });
  const legacyCsAudits = useCSAudits(undefined, { enabled: !USE_GOBODO_API });

  const csAuditRecords = USE_GOBODO_API
    ? (myWorkQuery.data?.records ?? [])
    : (legacyCsAudits.data ?? []);
  const myWorkSource = USE_GOBODO_API ? myWorkQuery.data?.source : 'fallback';
  const isLoading = USE_GOBODO_API ? myWorkQuery.isPending : legacyCsAudits.isPending;

  const { data: accountIdToName } = useAccountIdToName(csAuditRecords as Record<string, unknown>[]);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState<'dueDate' | 'status' | 'client'>('dueDate');

  // Map audits to display format and filter to current user
  const myAudits = useMemo(() => {
    const currentUserName = userName?.toLowerCase() || '';
    const currentUserId = userId || '';
    const customSuffix = getCustomSuffix();

    return (csAuditRecords as Record<string, unknown>[])
      .map(audit => {
        const data = (audit.data as Record<string, unknown>) ?? audit;
        const auditor = String(g(audit, data, 'auditor', 'Auditor__c', 'custom__auditor') ?? '').trim();
        const clientId = getCsAuditClientAccountRef(audit);
        const clientName = resolveCSAuditListClientName(audit, customSuffix, accountIdToName);
        const phaseStatus = String(g(audit, data, 'phaseStatus', 'Phase_Status__c', 'PhaseStatus__c', 'custom__phase_status') ?? '');
        const rawDueDate = String(g(audit, data, 'dueDate', 'Due_Date__c', 'DueDate__c', 'custom__due_date') ?? '');
        const auditType = String(g(audit, data, 'auditTypeDisplayName', 'AuditTypeDisplayName__c', 'auditType', 'Audit_Type__c') ?? '');
        const name = String(g(audit, data, 'name', 'Name__c', 'Name') ?? audit.id ?? '');

        return {
          id: String(audit.id ?? audit.key ?? ''),
          name,
          auditor,
          clientId,
          clientName,
          phaseStatus,
          dueDate: formatCyberSweepDisplayDate(rawDueDate),
          dueDateRaw: rawDueDate,
          auditType,
          isOverdue: isOverdue(rawDueDate),
          isDueThisWeek: isDueThisWeek(rawDueDate),
        };
      })
      .filter(audit => {
        if (myWorkSource === 'traversal' || myWorkSource === 'index') return true;
        const auditorLower = audit.auditor.toLowerCase();
        const isMyAudit = auditorLower === currentUserName ||
                         audit.auditor === currentUserId ||
                         auditorLower.includes(currentUserName.split(' ')[0]?.toLowerCase() || '---');
        return isMyAudit;
      })
      .filter(audit => {
        // Apply search filter
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          return audit.name.toLowerCase().includes(q) ||
                 audit.clientName.toLowerCase().includes(q) ||
                 audit.auditType.toLowerCase().includes(q);
        }
        return true;
      })
      .filter(audit => {
        // Apply status filter
        if (statusFilter) {
          return audit.phaseStatus.toLowerCase().includes(statusFilter.toLowerCase());
        }
        return true;
      })
      .sort((a, b) => {
        // Sort
        switch (sortBy) {
          case 'dueDate':
            return (a.dueDateRaw || '9999').localeCompare(b.dueDateRaw || '9999');
          case 'status':
            return a.phaseStatus.localeCompare(b.phaseStatus);
          case 'client':
            return a.clientName.localeCompare(b.clientName);
          default:
            return 0;
        }
      });
  }, [csAuditRecords, accountIdToName, userId, userName, searchQuery, statusFilter, sortBy, myWorkSource]);

  // Calculate stats
  const stats = useMemo(() => ({
    total: myAudits.length,
    overdue: myAudits.filter(a => a.isOverdue).length,
    dueThisWeek: myAudits.filter(a => a.isDueThisWeek && !a.isOverdue).length,
    inProgress: myAudits.filter(a => a.phaseStatus.includes('In Progress')).length,
    completed: myAudits.filter(a => a.phaseStatus.includes('Completed') || a.phaseStatus.includes('Complete')).length,
  }), [myAudits]);

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'Not Started', label: 'Not Started' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Road Block', label: 'Road Block' },
    { value: 'Completed', label: 'Completed' },
  ];

  const sortOptions = [
    { value: 'dueDate', label: 'Due Date' },
    { value: 'status', label: 'Status' },
    { value: 'client', label: 'Client Name' },
  ];

  return (
    <div className="space-y-3">
      {/* Header - Compact */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">My Work</h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">({userName || 'Current User'})</span>
        </div>
      </div>

      {/* Stats Cards - Compact */}
      <div className="grid grid-cols-5 gap-2">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">Total</span>
            <FileText className="h-3.5 w-3.5 text-gray-400" />
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{stats.total}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">Overdue</span>
            <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
          </div>
          <p className="text-lg font-bold text-red-600 dark:text-red-400">{stats.overdue}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">Due Soon</span>
            <Calendar className="h-3.5 w-3.5 text-yellow-500" />
          </div>
          <p className="text-lg font-bold text-yellow-600 dark:text-yellow-400">{stats.dueThisWeek}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">In Progress</span>
            <Clock className="h-3.5 w-3.5 text-blue-500" />
          </div>
          <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{stats.inProgress}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">Completed</span>
            <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
          </div>
          <p className="text-lg font-bold text-green-600 dark:text-green-400">{stats.completed}</p>
        </div>
      </div>

      {/* Filters - Compact */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[180px]">
            <StandardSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search audits..."
              size="sm"
            />
          </div>
          <div className="w-48">
            <StandardSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={statusOptions}
              placeholder="Filter by status"
              size="sm"
            />
          </div>
          <div className="w-40">
            <StandardSelect
              value={sortBy}
              onChange={(v) => setSortBy(v as 'dueDate' | 'status' | 'client')}
              options={sortOptions}
              placeholder="Sort by"
              size="sm"
            />
          </div>
        </div>
      </div>

      {/* Audits List - Compact */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            My Audits ({myAudits.length})
          </h3>
        </div>

        {isLoading ? (
          <div className="px-4 py-8 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-2"></div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Loading...</p>
          </div>
        ) : myAudits.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Briefcase className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {searchQuery || statusFilter ? 'No audits match filters' : 'No audits assigned'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[calc(100vh-340px)] overflow-y-auto">
            {myAudits.map(audit => (
              <div
                key={audit.id}
                onClick={() => onViewAudit?.(audit.id)}
                className="px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
                        {audit.name}
                      </span>
                      {audit.isOverdue && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                          Overdue
                        </span>
                      )}
                      {!audit.isOverdue && audit.isDueThisWeek && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300">
                          Due Soon
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      <span>{audit.clientName}</span>
                      <span>•</span>
                      <span>{audit.auditType || 'No Type'}</span>
                      {audit.dueDate && (
                        <>
                          <span>•</span>
                          <span>Due: {audit.dueDate}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getPhaseStatusColor(audit.phaseStatus)}`}>
                      {audit.phaseStatus || 'Not Started'}
                    </span>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CyberSweepMyWork;
